import { useEffect, useState } from 'react'
import { closestCenter, DndContext } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core/dist/types'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { compact } from 'lodash-es'
import { GripVertical, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'

interface Item {
  value: string
  label: string
}

interface SortableListProps {
  options?: Array<Item>
  value?: Array<Item['value']> // New prop for initially selected items
  onSelectionChange?: (
    values: Array<Item['value']>,
    selectedItems: Array<Item>
  ) => void
  placeholder?: string
}

function SortableItem({
  item,
  index,
  onRemove,
}: {
  item: Item
  index: number
  onRemove: (index: number) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.value })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="list-none"
    >
      <Card>
        <CardContent className="flex items-center justify-between p-1">
          <GripVertical className="size-5 text-gray-500" />

          <span className="grow px-2 text-sm">{item.label}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onPointerDown={() => onRemove(index)}
            aria-label={`Remove ${item.label}`}
          >
            <X className="size-4" />
          </Button>
        </CardContent>
      </Card>
    </li>
  )
}

export function SortableList({
  options = [],
  value = [], // Default to empty array if not provided
  onSelectionChange,
  placeholder,
}: SortableListProps) {
  const [selectedItems, setSelectedItems] = useState<Array<Item>>([])

  useEffect(() => {
    // Update the selectedItems if value prop changes
    const newSelectedItems = compact(
      value?.map((v) => options?.find((o) => o.value === v))
    )
    setSelectedItems(newSelectedItems)
  }, [options, value])

  const handleSelectionChange = (updatedSelectedItems: Array<Item>) => {
    onSelectionChange?.(
      updatedSelectedItems.map((i) => i.value),
      updatedSelectedItems
    )
  }

  const handleAddItem = (value: string) => {
    const selectedOption = options.find((i) => i.value === value)
    if (!selectedOption) return

    if (!selectedItems.find((item) => item.value === selectedOption.value)) {
      const newItems = [...selectedItems, selectedOption]
      setSelectedItems(newItems)
      handleSelectionChange(newItems)
    }
  }

  const handleRemoveItem = (index: number) => {
    const newItems = [...selectedItems]
    newItems.splice(index, 1)
    setSelectedItems(newItems)
    handleSelectionChange(newItems)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = selectedItems.findIndex(
        (item) => item.value === active.id
      )
      const newIndex = selectedItems.findIndex((item) => item.value === over.id)

      const newItems = arrayMove(selectedItems, oldIndex, newIndex)
      setSelectedItems(newItems)
      handleSelectionChange(newItems)
    }
  }

  // Filter available options to exclude already selected items
  const availableOptions = options.filter(
    (option) => !selectedItems.find((item) => item.value === option.value)
  )

  return (
    <>
      <Select onValueChange={handleAddItem}>
        <SelectTrigger className="mb-10 w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {availableOptions.map((item: Item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
            {availableOptions.length === 0 && (
              <SelectItem value={`${Math.random()}`} disabled>
                Nessuna opzione disponibile
              </SelectItem>
            )}
          </SelectGroup>
        </SelectContent>
      </Select>

      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={selectedItems.map((item) => item.value)}
          strategy={verticalListSortingStrategy}
        >
          <ul className="space-y-2">
            {selectedItems.map((item, index) => (
              <SortableItem
                key={item.value}
                item={item}
                index={index}
                onRemove={handleRemoveItem}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </>
  )
}
